import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { useGetProjectVersionChanges } from 'src/api/project'
import { IProjectInfo } from 'src/generated-types/project'
import { IPositionChange, IProjectVersionChanges } from 'src/generated-types/project-version-changes'
import { queryClient } from 'src/query-client'
import { RevisionFilter, indexChanges } from './projectVersionChanges.action'
import { isDraftVersion } from './projectVersion.action'
import { revisionTotals } from './revisionPricing.action'

interface RevisionChangesValue {
  isRevision: boolean
  changes?: IProjectVersionChanges
  changeByName: Map<string, IPositionChange>
  // Positions the draft removed, by their published name.
  removedByName: Map<string, IPositionChange>
  // What the revision's added and replaced parts and added work cost, as in the publish review.
  revisionCost?: number
  // The list is being reloaded after an edit.
  isUpdating: boolean
  filter: RevisionFilter
  setFilter: (filter: RevisionFilter) => void
  onlyChanged: boolean
  setOnlyChanged: (value: boolean) => void
}

const NO_CHANGES = new Map<string, IPositionChange>()

const RevisionChangesContext = createContext<RevisionChangesValue>({
  isRevision: false,
  changeByName: NO_CHANGES,
  removedByName: NO_CHANGES,
  isUpdating: false,
  filter: 'all',
  setFilter: () => undefined,
  onlyChanged: true,
  setOnlyChanged: () => undefined,
})

export const useRevisionChanges = () => useContext(RevisionChangesContext)

const REFETCH_DELAY_MS = 400

// What the open draft changed against the last published version, kept current as the user edits the project.
export const RevisionChangesProvider: React.FC<{ project: IProjectInfo; children: React.ReactNode }> = ({
  project,
  children,
}) => {
  const isRevision = isDraftVersion(project)
  const [filter, setFilter] = useState<RevisionFilter>('all')
  const [onlyChanged, setOnlyChanged] = useState(true)
  const {
    data: changes,
    refetch,
    isFetching,
  } = useGetProjectVersionChanges(project.customerId, project.localityId, project.id, isRevision)

  // Edits go through many different endpoints, so any finished mutation may have changed the revision.
  useEffect(() => {
    if (!isRevision) return undefined
    let timer: ReturnType<typeof setTimeout> | undefined
    const unsubscribe = queryClient.getMutationCache().subscribe(mutation => {
      if (mutation?.state.status !== 'success') return
      clearTimeout(timer)
      timer = setTimeout(() => refetch(), REFETCH_DELAY_MS)
    })
    return () => {
      clearTimeout(timer)
      unsubscribe()
    }
  }, [isRevision, refetch])

  const { changeByName, removedByName } = useMemo(() => indexChanges(changes), [changes])

  const revisionCost = useMemo(() => (changes ? revisionTotals(changes.positions).total : undefined), [changes])
  const isUpdating = isRevision && isFetching

  const value = useMemo(
    () => ({
      isRevision,
      changes,
      changeByName,
      removedByName,
      revisionCost,
      isUpdating,
      filter,
      setFilter,
      onlyChanged,
      setOnlyChanged,
    }),
    [isRevision, changes, changeByName, removedByName, revisionCost, isUpdating, filter, onlyChanged],
  )

  return <RevisionChangesContext.Provider value={value}>{children}</RevisionChangesContext.Provider>
}
